import {newSpecPage, SpecPage} from '@stencil/core/testing';
import {SelectableItem} from '../../JoySelectableItem/selectable-item';
import {SelectableItemGroup} from '../selectable-item-group';
import {Checkbox} from '../../JoyCheckbox/checkbox';

async function fireChange(page: SpecPage, item: HTMLElement) {
    const change = new Event('change');
    item.querySelector('input')!.dispatchEvent(change);
    return page.waitForChanges();
}

it('should set state by applying value on root element', async () => {
    const page = await newSpecPage({
        components: [SelectableItem, SelectableItemGroup],
        html: `
            <joy-selectable-item-group value="second">
                <joy-selectable-item value="first">First item</joy-selectable-item>
                <joy-selectable-item value="second">Second item</joy-selectable-item>
                <joy-selectable-item value="third">Third item</joy-selectable-item>
            </joy-selectable-item-group>          
        `,
    });

    await page.waitForChanges();

    const firstItem = page.body.querySelector('joy-selectable-item:nth-child(1)') as HTMLJoySelectableItemElement;
    const secondItem = page.body.querySelector('joy-selectable-item:nth-child(2)') as HTMLJoySelectableItemElement;
    const thirdItem = page.body.querySelector('joy-selectable-item:nth-child(3)') as HTMLJoySelectableItemElement;

    expect(firstItem.checked).toBe(false);
    expect(secondItem.checked).toBe(true);
    expect(thirdItem.checked).toBe(false);
});

it('should update single selectable-item by clicking on other items', async () => {
    const page = await newSpecPage({
        components: [SelectableItem, SelectableItemGroup],
        html: `
            <joy-selectable-item-group>
                <joy-selectable-item value="first">First item</joy-selectable-item>
                <joy-selectable-item value="second">Second item</joy-selectable-item>
                <joy-selectable-item value="third">Third item</joy-selectable-item>
            </joy-selectable-item-group>          
        `,
    });

    const firstItem = page.body.querySelector('joy-selectable-item:nth-child(1)') as HTMLElement;
    const secondItem = page.body.querySelector('joy-selectable-item:nth-child(2)') as HTMLElement;
    const thirdItem = page.body.querySelector('joy-selectable-item:nth-child(3)') as HTMLElement;

    // Click on the first item
    await fireChange(page, firstItem);

    expect(firstItem.querySelector('.joy-selectable-item--checked')).not.toBeNull();
    expect(secondItem.querySelector('.joy-selectable-item--checked')).toBeNull();
    expect(thirdItem.querySelector('.joy-selectable-item--checked')).toBeNull();

    // Click on the second item
    await fireChange(page, secondItem);

    expect(firstItem.querySelector('.joy-selectable-item--checked')).toBeNull();
    expect(secondItem.querySelector('.joy-selectable-item--checked')).not.toBeNull();
    expect(thirdItem.querySelector('.joy-selectable-item--checked')).toBeNull();

    // Click on the last item
    await fireChange(page, thirdItem);

    expect(firstItem.querySelector('.joy-selectable-item--checked')).toBeNull();
    expect(secondItem.querySelector('.joy-selectable-item--checked')).toBeNull();
    expect(thirdItem.querySelector('.joy-selectable-item--checked')).not.toBeNull();
});

it('should unselect item in single choice scenario, when clicking on the same item', async () => {
    const page = await newSpecPage({
        components: [SelectableItem, SelectableItemGroup],
        html: `
            <joy-selectable-item-group>
                <joy-selectable-item checked value="first">First item</joy-selectable-item>
                <joy-selectable-item value="second">Second item</joy-selectable-item>
                <joy-selectable-item value="third">Third item</joy-selectable-item>
            </joy-selectable-item-group>          
        `,
    });

    const firstItem = page.body.querySelector('joy-selectable-item:nth-child(1)') as HTMLElement;
    expect(firstItem.querySelector('.joy-selectable-item--checked')).not.toBeNull();

    firstItem.click();
    await page.waitForChanges();
    expect(firstItem.querySelector('.joy-selectable-item--checked')).toBeNull();
});

it('should update multiple selectable-item-group by clicking on other items', async () => {
    async function getMultipleChoiceValue(page: SpecPage) {
        return page.rootInstance.getSelectedItemsValue();
    }

    const page = await newSpecPage({
        components: [SelectableItem, SelectableItemGroup, Checkbox],
        html: `
            <joy-selectable-item-group multiple>
                <joy-selectable-item value="1">Category title 1</joy-selectable-item>
                <joy-selectable-item checked value="2">Category title 2</joy-selectable-item>
                <joy-selectable-item disabled value="3">Category title 3</joy-selectable-item>
                <joy-selectable-item value="4">Category title 4</joy-selectable-item>
                <joy-selectable-item checked value="5">Category title 5</joy-selectable-item>
            </joy-selectable-item-group>        
        `,
    });

    await page.waitForChanges();
    const value = await page.rootInstance.getSelectedItemsValue();
    expect(value).toEqual(['2', '5']);

    // Select the fourth item in the list
    const fourthItem = page.body.querySelector('joy-selectable-item:nth-child(4)') as HTMLJoySelectableItemElement;
    await fireChange(page, fourthItem);
    await page.waitForChanges();
    const updatedValue = await getMultipleChoiceValue(page);
    expect(updatedValue).toEqual(['2', '4', '5']);

    // Select the first item in the list
    const firstItem = page.body.querySelector('joy-selectable-item:nth-child(1)') as HTMLJoySelectableItemElement;
    await fireChange(page, firstItem);
    await page.waitForChanges();
    const addingFirstItemValue = await getMultipleChoiceValue(page);
    expect(addingFirstItemValue).toEqual(['1', '2', '4', '5']);

    // Trying to select the third item in the list but it's disabled so no changes
    const thirdItem = page.body.querySelector('joy-selectable-item:nth-child(3)') as HTMLJoySelectableItemElement;
    await fireChange(page, thirdItem);
    await page.waitForChanges();
    const thirdItemValue = await getMultipleChoiceValue(page);
    expect(thirdItemValue).toEqual(['1', '2', '4', '5']);
});

it('should fire event on item-group when changing any item', async () => {
    const page = await newSpecPage({
        components: [SelectableItem, SelectableItemGroup, Checkbox],
        html: `
            <joy-selectable-item-group multiple>
                <joy-selectable-item value="1">Category title 1</joy-selectable-item>
                <joy-selectable-item checked value="2">Category title 2</joy-selectable-item>
                <joy-selectable-item disabled value="3">Category title 3</joy-selectable-item>
                <joy-selectable-item value="4">Category title 4</joy-selectable-item>
                <joy-selectable-item checked value="5">Category title 5</joy-selectable-item>
            </joy-selectable-item-group>        
        `,
    });

    const spyChange = jest.spyOn(page.rootInstance.joySelectableItemGroupChange, 'emit');
    const firstItem = page.body.querySelector('joy-selectable-item:nth-child(1)') as HTMLJoySelectableItemElement;
    const secondItem = page.body.querySelector('joy-selectable-item:nth-child(2)') as HTMLJoySelectableItemElement;
    const thirdItem = page.body.querySelector('joy-selectable-item:nth-child(3)') as HTMLJoySelectableItemElement;
    const fourthItem = page.body.querySelector('joy-selectable-item:nth-child(4)') as HTMLJoySelectableItemElement;

    await fireChange(page, firstItem);
    await fireChange(page, secondItem);
    await fireChange(page, thirdItem);
    await fireChange(page, fourthItem);

    expect(spyChange).toHaveBeenCalledTimes(3);
});

it('should update group state when changing the .value property', async () => {
    const page = await newSpecPage({
        components: [SelectableItem, SelectableItemGroup, Checkbox],
        html: `
            <joy-selectable-item-group multiple>
                <joy-selectable-item value="1">Category title 1</joy-selectable-item>
                <joy-selectable-item value="2">Category title 2</joy-selectable-item>
                <joy-selectable-item value="3">Category title 3</joy-selectable-item>
            </joy-selectable-item-group>        
        `,
    });

    (page.rootInstance as HTMLJoySelectableItemGroupElement).value = ['1', '3'];
    await page.waitForChanges();

    const firstItem = page.body.querySelector('joy-selectable-item:nth-child(1)') as HTMLJoySelectableItemElement;
    const secondItem = page.body.querySelector('joy-selectable-item:nth-child(2)') as HTMLJoySelectableItemElement;
    const thirdItem = page.body.querySelector('joy-selectable-item:nth-child(3)') as HTMLJoySelectableItemElement;

    expect(firstItem.checked).toBe(true);
    expect(secondItem.checked).toBe(false);
    expect(thirdItem.checked).toBe(true);
});
